import { Fragment, useEffect, useId, useSyncExternalStore } from "react";
import { useLocation, useNavigate, useNavigation } from "react-router";
import { useTranslation } from "react-i18next";
import type { i18n as I18n } from "i18next";
import { cn } from "~/lib/cn";
import { Button } from "./Button";
import { SUPPORTED_LANGS, type Lang } from "~/config/route-config";
import { swapLocaleInPath } from "~/lib/locale-path";
import { startsInAppNavigation, useNavPendingTo } from "~/components/layout/use-nav-pending";
import { NavPendingSweep } from "~/components/ui/NavPendingSweep";

type Variant = "light" | "dark" | "pill" | "text";

interface LanguageSwitcherProps {
  variant?: Variant;
  className?: string;
}

/**
 * The language hop in flight, shared by every selector on the page (the header, the cúter,
 * the footer, the account menu): one request at a time, wherever it was started
 * (navigation brief, 2026-10-10 § D). `failed` keeps the error until the next attempt or
 * until she moves to another page (`at` is where it failed).
 */
type Hop = { to: Lang; from: Lang; failed: boolean; owner: string; at: string } | null;
let hop: Hop = null;
const listeners = new Set<() => void>();
const setHop = (next: Hop) => {
  hop = next;
  listeners.forEach((fn) => fn());
};
const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};
const readHop = () => hop;
const readNoHop = () => null;

/**
 * The new language's strings for the namespaces this page already uses, fetched BEFORE the
 * navigation: the page and the active language stay as they are while it loads, and when
 * the new route commits the root switches i18next in the same frame (its layout effect),
 * with nothing left to fetch. A failure leaves everything where it was.
 */
function preloadLanguage(i18n: I18n, lng: Lang): Promise<void> {
  const connector = (i18n.services as { backendConnector?: { load?: (l: string[], n: string[], cb: (err?: unknown) => void) => void } } | undefined)
    ?.backendConnector;
  const namespaces = (i18n as I18n & { reportNamespaces?: { getUsedNamespaces(): string[] } }).reportNamespaces?.getUsedNamespaces() ?? [];
  if (!connector?.load || namespaces.length === 0) return Promise.resolve();
  return new Promise((resolve, reject) => {
    connector.load!([lng], namespaces, (err) => (err && (!Array.isArray(err) || err.length > 0) ? reject(err) : resolve()));
  });
}

export function LanguageSwitcher({
  variant = "light",
  className,
}: LanguageSwitcherProps) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const navigation = useNavigation();
  const current = hopFrom(i18n.language);
  const pendingHop = useSyncExternalStore(subscribe, readHop, readNoHop);
  // Only the selector she pressed shows the line and speaks; the others just stand by.
  const owner = useId();
  const mine = pendingHop?.owner === owner;

  /** Where `locale` takes her: the same page in that language, query and section kept. */
  const targetFor = (locale: Lang, hash = location.hash) =>
    `${swapLocaleInPath(location.pathname, locale)}${location.search}${hash}`;

  /**
   * The section she is on at the moment of the click. An in-page anchor writes its fragment
   * with `history.pushState` (`useSmoothAnchors`, so the router does not re-scroll), and the
   * router never hears it: its `location.hash` still names the section she arrived with
   * (N3, Astra's second audit — `/en/method` → «Explore the four phases» → ES lost `#metodo`).
   * The address bar is read only while it shows the router's own page and query.
   */
  const liveHash = () =>
    typeof window !== "undefined" &&
    window.location.pathname === location.pathname &&
    window.location.search === location.search
      ? window.location.hash
      : location.hash;

  // Busy while a hop fetches its strings or its route — from any selector on the page. The
  // fragment is not compared: it is read at the click, and it names no other page.
  const routing =
    navigation.state !== "idle" &&
    navigation.location != null &&
    SUPPORTED_LANGS.some(
      (l) => l !== current && targetFor(l, "") === `${navigation.location!.pathname}${navigation.location!.search}`,
    );
  const busy = (pendingHop != null && !pendingHop.failed) || routing;

  // The header and footer selectors outlive a route change, so a failed hop's message is
  // dropped once she is on another page (AI review on #2063, round 4).
  useEffect(() => {
    if (hop?.failed && hop.at !== location.pathname) setHop(null);
  }, [location.pathname]);

  const switchLanguage = (newLang: Lang) => {
    // The active language starts nothing, and a hop in flight is not doubled.
    if (newLang === i18n.language || busy) return;
    const target = targetFor(newLang, liveHash());
    setHop({ to: newLang, from: current, failed: false, owner, at: location.pathname });
    preloadLanguage(i18n, newLang).then(
      () => {
        void Promise.resolve(navigate(target)).finally(() => {
          if (hop?.to === newLang) setHop(null);
        });
      },
      () => setHop({ to: newLang, from: current, failed: true, owner, at: location.pathname }),
    );
  };

  // The one announcement, outside the group, in the language she is leaving; an error
  // keeps the page and her language and invites a retry.
  const status =
    pendingHop && mine && pendingHop.from === current ? (
      <span className="sr-only" role={pendingHop.failed ? "alert" : "status"}>
        {pendingHop.failed ? t("language.error") : t(`language.switching.${pendingHop.to}`)}
      </span>
    ) : null;

  // T872 S1 — the `ES / EN` row (design pass, 2026-10-08, the founder's explicit
  // correction): no visible «Idioma» label, no long names, the active language in bold,
  // full accessible names, 44px targets. N6 (Astra's second audit): it announced a radio
  // group whose arrows did nothing. Changing language is a navigation — to the same page in
  // the other language — and an arrow key must not start one, so the row is two links, the
  // active one `aria-current`, Tab between them, Enter follows (the pill keeps its radios).
  if (variant === "text") {
    return (
      <>
      <div
        className={cn("lang-text", className)}
        role="group"
        aria-label={t("language.switch")}
        aria-busy={busy || undefined}
      >
        {SUPPORTED_LANGS.map((locale, idx) => (
          <Fragment key={locale}>
            {idx > 0 ? (
              <span className="sep" aria-hidden="true">
                /
              </span>
            ) : null}
            <LangTextLink
              locale={locale}
              active={i18n.language === locale}
              href={targetFor(locale)}
              label={t(`language.${locale}`)}
              onSwitch={switchLanguage}
              pending={mine && pendingHop?.to === locale && !pendingHop.failed}
            />
          </Fragment>
        ))}
      </div>
      {status}
      {pendingHop?.failed && mine && pendingHop.from === current ? (
        <p className={cn("lang-text-error", className)} aria-hidden="true">{t("language.error")}</p>
      ) : null}
      </>
    );
  }

  // Segmented-pill (rebrand `.lang`) — used on dark chrome (footer / header).
  if (variant === "pill") {
    return (
      <>
      <div
        className={cn("lang", className)}
        role="radiogroup"
        aria-label={t("language.switch")}
        aria-busy={busy || undefined}
      >
        {SUPPORTED_LANGS.map((locale) => (
          <LangPillButton
            key={locale}
            locale={locale}
            active={i18n.language === locale}
            // Resolved only for the segment that can actually navigate. The active one
            // passes null, so `swapLocaleInPath` — which walks ROUTE_CONFIG — is not run to
            // produce a value the hook would discard.
            to={i18n.language === locale ? null : targetFor(locale)}
            label={t(`language.${locale}`)}
            onSwitch={switchLanguage}
            pending={mine && pendingHop?.to === locale && !pendingHop.failed}
          />
        ))}
      </div>
      {status}
      </>
    );
  }

  return (
    <div
      className={cn("flex items-center gap-1", className)}
      role="radiogroup"
      aria-label={t("language.switch")}
    >
      {SUPPORTED_LANGS.map((locale, idx) => (
        <span key={locale} className="flex items-center gap-1">
          {idx > 0 && (
            <span
              className={cn(
                "text-[10px]",
                variant === "dark" ? "text-textMuted" : "text-textSecondary",
              )}
            >
              |
            </span>
          )}
          <Button
            variant="unstyled"
            type="button"
            onClick={() => switchLanguage(locale)}
            className={cn(
              "text-[10px] font-bold uppercase tracking-widest transition-opacity",
              i18n.language === locale
                ? variant === "dark"
                  ? "text-white"
                  : "text-textMain"
                : variant === "dark"
                  ? "text-textMuted hover:text-white"
                  : "text-textSecondary hover:opacity-70",
            )}
            role="radio"
            aria-checked={i18n.language === locale}
            aria-label={t(`language.${locale}`)}
          >
            {locale}
          </Button>
        </span>
      ))}
    </div>
  );
}

/**
 * One segment of the pill, isolated so it can own a `useNavPendingTo` call.
 *
 * The founder asked for this while testing T633's contrast fix on desktop: the language tap
 * left her unsure whether the button had registered, during the moment the app takes to
 * switch. That is the T556/T558 failure mode arriving on a control those tickets did
 * not reach — a server hop with no acknowledgment, so the member re-taps. This wires the
 * SAME idiom rather than a new one: `useNavPendingTo` + `.nav-pending` + `NavPendingSweep`,
 * the gesture already carried by the member header's links and the shell's back control.
 *
 * A component per segment because the hook cannot be called from inside `.map()` — the same
 * reason `Button.tsx` isolates `NavPendingButtonLink`.
 *
 * The ACTIVE segment passes `to = null` and never lights: clicking it returns early in
 * `switchLanguage`, so no navigation starts and a lit control would be claiming someone
 * else's — the R-T558-2 failure the decaying stamp exists to bound.
 */
function LangPillButton({
  locale,
  active,
  to,
  label,
  onSwitch,
  pending: pendingHop = false,
}: {
  locale: Lang;
  active: boolean;
  /** `null` for the active segment — it navigates nowhere, so it can never light. */
  to: string | null;
  label: string;
  onSwitch: (locale: Lang) => void;
  /** This selector's own hop is fetching this language (before the router moves). */
  pending?: boolean;
}) {
  const { pending: routing, onNavigate } = useNavPendingTo(to);
  const pending = routing || pendingHop;

  return (
    <Button
      variant="unstyled"
      type="button"
      role="radio"
      aria-checked={active}
      aria-label={label}
      aria-busy={pending || undefined}
      className={cn(pending && "nav-pending")}
      onClick={() => {
        // The stamp before `onSwitch`: the hop's navigation is this button's own (R-T558-2).
        if (!active) onNavigate();
        onSwitch(locale);
      }}
    >
      {locale.toUpperCase()}
      {pending ? <NavPendingSweep /> : null}
    </Button>
  );
}

/**
 * One language of the `ES / EN` row: a link to the same page in that language (`hrefLang`),
 * so a modified click opens it in a new tab and a reader hears a link. Its name is written in
 * the page's language and starts with the visible code («EN, inglés»): no `lang` on the link,
 * which would read a Spanish word with an English voice, and the name contains what is seen
 * (WCAG 2.5.3 — a voice user says «EN»). A plain click stays in the app: `onSwitch` preloads the strings,
 * then navigates. The active one is `aria-current` and goes nowhere.
 */
function LangTextLink({
  locale,
  active,
  href,
  label,
  onSwitch,
  pending: pendingHop = false,
}: {
  locale: Lang;
  active: boolean;
  href: string;
  label: string;
  onSwitch: (locale: Lang) => void;
  /** This selector's own hop is fetching this language (before the router moves). */
  pending?: boolean;
}) {
  const { pending: routing, onNavigate } = useNavPendingTo(active ? null : href);
  const pending = routing || pendingHop;

  return (
    <a
      href={href}
      hrefLang={locale}
      aria-label={label}
      aria-current={active ? "true" : undefined}
      aria-busy={pending || undefined}
      className={cn(pending && "nav-pending")}
      onClick={(event) => {
        if (!startsInAppNavigation(event)) return;
        event.preventDefault();
        // The stamp before `onSwitch`: the hop's navigation is this link's own (R-T558-2).
        if (!active) onNavigate();
        onSwitch(locale);
      }}
    >
      {locale.toUpperCase()}
      {pending ? <NavPendingSweep /> : null}
    </a>
  );
}

function hopFrom(language: string): Lang {
  return language === "en" ? "en" : "es";
}
